import { useEffect, useMemo, useRef, useState } from 'react';
import { getDay, parseISO } from 'date-fns';
import Agendamentos from '../../services/Agendamentos';
import { SERIES_DEFAULT_SESSIONS, buildOccurrences, buildSessions, generateDates, isValidDate, validateSessions } from '../../utilities/agendamentoSeries';

const CHECK_IDLE = { state: 'idle', byKey: {}, message: null };
const OCCUPIED_REASONS = ['SCHEDULE_CONFLICT', 'ALREADY_SCHEDULED'];

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// O índice de cada conflito é a posição na lista enviada; volta para a chave da linha
const conflictsByKey = (list, conflicts = []) => Object.fromEntries(
  conflicts.filter((conflict) => list[conflict.index]).map((conflict) => [list[conflict.index].key, conflict]),
);

// Estado da série do modal de agendamento. Fica fora do formulário da Agenda,
// que também guarda os filtros e é limpo pelo botão Limpar
export function useAgendamentoSeries({ main, tecnicoId, pacienteId }) {
  const [enabled, setEnabled] = useState(false);
  const [weekDays, setWeekDays] = useState([]);
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [sessionsCount, setSessionsCount] = useState(SERIES_DEFAULT_SESSIONS);
  const [skipped, setSkipped] = useState([]); // datas geradas que foram puladas
  const [extraDates, setExtraDates] = useState([]); // datas avulsas, fora da regra
  const [adjustments, setAdjustments] = useState({}); // { [key]: { date?, start_time?, end_time? } }
  const [check, setCheck] = useState(CHECK_IDLE);
  const sequence = useRef(0);

  // Pulos, avulsas e ajustes ficam guardados pela data: mudar a regra não os apaga
  const { dates, skippedInRule, requested } = useMemo(
    () => generateDates({ firstDate: main.date, weekDays, intervalWeeks, count: sessionsCount, skipped }),
    [main.date, weekDays, intervalWeeks, sessionsCount, skipped],
  );

  const sessions = useMemo(
    () => (enabled ? buildSessions({ dates, extraDates, startTime: main.start_time, endTime: main.end_time, adjustments }) : []),
    [enabled, dates, extraDates, main.start_time, main.end_time, adjustments],
  );

  const marks = useMemo(() => validateSessions(sessions), [sessions]);

  // Só as linhas sem erro na tela vão para a verificação
  const sent = useMemo(() => sessions.filter((session) => marks[session.key]?.type !== 'error'), [sessions, marks]);
  const body = JSON.stringify(buildOccurrences(sent));
  const sentKeys = sent.map((session) => session.key).join('|');

  // Confere a agenda do doutor meio segundo depois da última mudança, sem gravar
  useEffect(() => {
    const mine = ++sequence.current;

    if (!enabled || sessions.length < 2) {
      setCheck(CHECK_IDLE);
      return undefined;
    }

    if (!tecnicoId) {
      setCheck({ ...CHECK_IDLE, message: 'Escolha o doutor para conferir a agenda.' });
      return undefined;
    }

    if (sent.length === 0) {
      setCheck({ state: 'waiting', byKey: {}, message: 'Corrija as linhas em vermelho para conferir a agenda.' });
      return undefined;
    }

    const list = sent;
    setCheck({ state: 'checking', byKey: {}, message: null });

    const timer = setTimeout(() => {
      Agendamentos.checkBatch({ tecnico_id: tecnicoId, paciente_id: pacienteId || undefined, occurrences: JSON.parse(body) })
        .then(({ data }) => {
          if (mine === sequence.current) setCheck({ state: 'ready', byKey: conflictsByKey(list, data.conflicts), message: null });
        })
        .catch((error) => {
          if (mine !== sequence.current) return;

          // 4xx: o servidor diz o que recusou; sem resposta: falha de rede
          const message = error?.response?.data?.message ?? 'Não foi possível verificar agora. A agenda é conferida de novo ao salvar.';
          setCheck({ state: 'failed', byKey: {}, message });
        });
    }, 500);

    return () => clearTimeout(timer);
  }, [enabled, tecnicoId, pacienteId, body, sentKeys, sessions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  function enable(value) {
    setEnabled(value);

    // Ao ligar, já marca o dia da semana da 1ª sessão (domingo fica de fora)
    if (value && weekDays.length === 0 && isValidDate(main.date) && getDay(parseISO(main.date)) !== 0)
      setWeekDays([getDay(parseISO(main.date))]);
  }

  // Linha gerada: pula (a série repõe no fim). Linha avulsa: remove
  function remove(key) {
    if (key.startsWith('extra:')) {
      setExtraDates((current) => current.filter((date) => `extra:${date}` !== key));
      setAdjustments(({ [key]: _removed, ...rest }) => rest);
    } else {
      setSkipped((current) => (current.includes(key) ? current : [...current, key]));
    }
  }

  const undoSkip = (date) => setSkipped((current) => current.filter((value) => value !== date));

  const addExtraDate = (date) => {
    if (isValidDate(date)) setExtraDates((current) => (current.includes(date) ? current : [...current, date]));
  };

  const adjust = (key, fields) => setAdjustments((current) => ({ ...current, [key]: { ...current[key], ...fields } }));

  // Volta a linha para o horário de cima, mantendo a data trocada, se houver
  const resetTime = (key) => setAdjustments(({ [key]: current = {}, ...rest }) => (current.date ? { ...rest, [key]: { date: current.date } } : rest));

  // "Ocupada" é conflito na agenda do doutor; erros da lista contam à parte
  const occupied = (session) => OCCUPIED_REASONS.includes(check.byKey[session.key]?.reason);

  // A 1ª sessão (campos de cima) não é pulada
  const skipOccupied = () => sessions.forEach((session) => {
    if (!session.main && occupied(session)) remove(session.key);
  });

  // Depois de uma recusa da gravação. Conflito de agenda: a gravação conferiu todas as sessões e
  // o resultado substitui a verificação. Outras recusas: só marcam as linhas indicadas
  const markConflicts = (conflicts, list, { replace = true } = {}) => {
    if (!replace) {
      setCheck((current) => ({ ...current, byKey: { ...current.byKey, ...conflictsByKey(list, conflicts) } }));
      return;
    }

    sequence.current += 1; // descarta uma verificação que ainda esteja em andamento
    setCheck({ state: 'ready', byKey: conflictsByKey(list, conflicts), message: null });
  };

  function clear() {
    setEnabled(false);
    setWeekDays([]);
    setIntervalWeeks(1);
    setSessionsCount(SERIES_DEFAULT_SESSIONS);
    setSkipped([]);
    setExtraDates([]);
    setAdjustments({});
    setCheck(CHECK_IDLE);
  }

  // "Salvar e agendar próximas": série nova, já ligada, com o dia da semana da sessão de origem
  function startNext(weekDay) {
    clear();
    setEnabled(true);
    setWeekDays(weekDay ? [weekDay] : []);
  }

  const mainMark = sessions.length > 0 && marks[sessions[0].key]?.type === 'error' && marks[sessions[0].key].label !== 'Repetida' ? marks[sessions[0].key] : null;
  const occupiedCount = sessions.filter(occupied).length;
  const skippableCount = sessions.filter((session) => !session.main && occupied(session)).length;
  const warningCount = sessions.filter((session) => check.byKey[session.key]?.reason === 'PACIENTE_SAME_DAY').length;
  const freeCount = sessions.filter((session) => !check.byKey[session.key] && marks[session.key]?.type !== 'error').length;
  const errorCount = sessions.filter((session) => marks[session.key]?.type === 'error' || (check.byKey[session.key]?.blocking && !occupied(session))).length;
  const pastCount = sessions.filter((session) => marks[session.key]?.label === 'Data passada').length;
  const allAlreadyScheduled = check.state === 'ready' && sessions.length > 1 && sessions.every((session) => check.byKey[session.key]?.reason === 'ALREADY_SCHEDULED');
  const stoppedAtHorizon = weekDays.length > 0 && dates.length > 0 && dates.length < requested;

  // Motivo que impede salvar, escrito ao lado do botão
  function blockingReason() {
    if (!enabled) return null;
    if (sessions.length === 0) return 'Informe a data da 1ª sessão.';
    if (mainMark) return `${mainMark.detail ?? `${mainMark.label}.`} Corrija nos campos acima.`;
    if (sessions.length < 2) return 'Marque ao menos um dia da semana ou adicione uma data.';
    if (allAlreadyScheduled) return `Estas ${sessions.length} sessões já estão agendadas para este paciente.`;
    if (occupied(sessions[0])) return 'A 1ª sessão está ocupada: mude a data ou o horário acima.';
    if (check.byKey[sessions[0].key]?.reason === 'OUTSIDE_SERVICE_HOURS') return 'A 1ª sessão está fora do horário do doutor: mude a data ou o horário acima.';
    if (occupiedCount) return `Resolva ${plural(occupiedCount, 'data ocupada', 'datas ocupadas')} para salvar.`;
    if (errorCount) return `Corrija ${plural(errorCount, 'data marcada', 'datas marcadas')} em vermelho para salvar.`;
    return null;
  }

  const blocking = blockingReason();
  const reminder = !blocking && pastCount ? `${plural(pastCount, 'sessão fica', 'sessões ficam')} no passado.` : null;

  return {
    enabled, enable,
    weekDays, setWeekDays, intervalWeeks, setIntervalWeeks, sessionsCount, setSessionsCount,
    sessions, marks, check, skippedInRule, extraDates,
    requested, generatedCount: dates.length, stoppedAtHorizon,
    remove, undoSkip, addExtraDate, adjust, resetTime, skipOccupied, markConflicts, startNext, clear,
    mainMark, occupiedCount, skippableCount, warningCount, freeCount, allAlreadyScheduled,
    blocking, reminder,
  };
}
